import {
  type CatalogueItemResource,
  CatalogueItemsController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconBuildingStore, IconCircleCheck, IconFileUpload, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { useSession } from '@/auth/session';
import { formatPaise } from '@/lib/money';

import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { cataloguePricesPath, outletCataloguePath, paths } from '../paths';
import {
  CATALOGUE_KIND_ITEMS,
  CATALOGUE_KINDS,
  type CatalogueKind,
  isRetired,
  labelForKind,
} from './catalogue-kinds';
import { EditCatalogueItemDialog } from './EditCatalogueItemDialog';
import { NewCatalogueItemDialog } from './NewCatalogueItemDialog';

/**
 * What the garden sells: every product, ticket type and activity, and the way to
 * add one.
 *
 * ## This list is paged, and that is a decision rather than a default
 *
 * `docs/ui-conventions.md`, "Reading a list", asks for the strategy to be picked
 * deliberately, and every other back-office screen reads whole through
 * `collectPages`. This one must not. An outlet list is bounded by the park —
 * a dozen places, and fifty would be remarkable — but a catalogue grows with
 * *use*: every seed packet, every ticket type, every activity, for as long as the
 * garden sells things, and nothing is ever deleted because a retired item keeps
 * its code and its price history for Reporting. Read whole, this screen would be
 * a loop that gets one request longer every few months and never announces it.
 *
 * The **outlets** beside it are read whole, from the same file, which is the same
 * rule applied to a different set rather than an inconsistency: they fill a
 * filter and a picker, both of which need every option at once, and there are a
 * dozen of them.
 *
 * ## Cost price is on no read, for anybody
 *
 * The ticket asks for cost to appear for an owner and be absent for everyone
 * else. **It is absent for everyone, including the owner, and that is the API's
 * decision rather than this screen's.** `GET /v1/catalogue/items` answers
 * `CatalogueItemResource`, which has no `costPaise` field at all — not the field
 * set to null — and `CatalogueItemsController.list` says why in as many words:
 * "a shape that is identical for every caller cannot hint that a field was
 * withheld". The only place cost appears in a response is the create's own 201
 * and the price history.
 *
 * So there is no cost column here, and no code that would render one if a value
 * turned up. The owner's cost affordance is the field on the create form, and
 * that form is reachable only by an owner — which is the asymmetry the ticket
 * asked for, arriving one screen earlier than it expected. Flagged in the PR
 * body; if cost is genuinely wanted in the list it needs an API change, not a
 * cast here.
 */

/** Every state the rows can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly items: readonly CatalogueItemResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The value the two filters carry when they are not filtering. */
const ANY = 'ALL';

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS_WITHOUT_STATUS = 4;

export function Catalogue() {
  const client = useApiClient();
  const { session } = useSession();

  // Every write here is `@RequireRole('OWNER')` on the API — the whole
  // controller class carries it and only the two reads step out of it — so a
  // manager gets the list and no way to add to it, edit one, retire one or put
  // one back. Hidden rather than shown and 403'd, per docs/ui-conventions.md;
  // the server refuses regardless, because this role came out of
  // `sessionStorage` and is editable by whoever is at the keyboard.
  //
  // One flag rather than four: the class-level decorator is what makes them the
  // same answer, so four names would suggest four rules that could diverge.
  const mayWrite = session?.user.role === 'OWNER';

  // The same test as `mayWrite` and the same answer today, and deliberately
  // **not** folded into it. The argument above for one flag rather than four is
  // right and does not reach this one: those four are four writes on
  // `CatalogueItemsController`, made one answer by one class-level decorator, so
  // they cannot diverge without that decorator moving. This is a different
  // controller and it is not a write.
  //
  // `CataloguePricesController` carries its own `@RequireRole('OWNER')`, and
  // where the items controller lets `list` and `read` step out of it, this one
  // lets nothing out: **the reads are owner-only too**, because every price row
  // carries `costPaise`. So "may write to the catalogue" and "may read a price
  // history" are two rules that happen to agree, and a screen that read the
  // second off the first would go quietly wrong the day either decorator moves —
  // which is exactly what a manager being shown a link to a screen of cost
  // prices looks like.
  //
  // Hidden rather than shown and refused, per `docs/ui-conventions.md`.
  // Courtesy and not security: the role came out of `sessionStorage`, the route
  // guard declares `requireRole('OWNER')` again, and the server refuses
  // regardless.
  const mayReadPrices = session?.user.role === 'OWNER';

  const [kind, setKind] = useState<CatalogueKind | typeof ANY>(ANY);
  const [outletId, setOutletId] = useState<string>(ANY);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [offset, setOffset] = useState(0);

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // The window the last successful read answered with, kept **outside**
  // `ListState` on purpose: it survives the next load, so the pager and the
  // count stay where they are while a page is in flight instead of vanishing
  // and coming back. A control that disappears under the pointer between two
  // presses of Next is the same failure `TableSkeleton` exists to prevent, one
  // element lower down the page. `null` until the first answer, because a pager
  // reading "0 items" before anything has been asked is worse than no pager.
  const [page, setPage] = useState<PageState | null>(null);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [creating, setCreating] = useState(false);

  // The row currently open, or none. The item itself rather than its id, so the
  // dialog renders from the row that was clicked and does not have to search a
  // list that may reload underneath it.
  const [editing, setEditing] = useState<CatalogueItemResource | null>(null);

  // What just happened, said once above the table. One slot rather than a
  // boolean per event: creating, editing, retiring and restoring are the same
  // announcement in different words, and no two are true of the same moment.
  const [notice, setNotice] = useState<{ code: string; text: string } | null>(null);

  // Bumping this re-runs the load. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  // Any filter moving puts the list back at the first page. Left alone, a filter
  // applied at offset 200 lands on a page past the end of a shorter result and
  // reads as "nothing matched" — `pageAfter` clamps a *step*, and this is not
  // one.
  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setOffset(0);
  }, []);

  useEffect(() => {
    // Two guards, not one. `controller` stops the request itself the moment a
    // filter moves, and `live` stops a response that was already in flight from
    // being written into state after this effect was torn down — without it,
    // changing a filter twice quickly can land the first answer on top of the
    // second and leave the screen showing rows the filters exclude.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(CatalogueItemsController_list_v1, {
        query: {
          offset,
          limit: ROWS_PER_PAGE,

          // Each filter is sent only when it is filtering. The API's defaults
          // are all three off, and the query string is what appears in a log
          // when somebody is working out what a screen asked for.
          ...(kind === ANY ? {} : { kind }),
          ...(outletId === ANY ? {} : { outletId }),
          ...(includeRetired ? { includeRetired: true } : {}),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({ kind: 'loaded', items: answer.items });

          // The window the *server* answered with, not the one that was asked
          // for. They are the same today, and a pager that assumed so would be
          // silently wrong the day the API clamps a limit.
          setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
        }
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, kind, outletId, includeRetired, offset, reloads]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: a dozen outlets fill a filter and a picker, both of which need
    // every option at once. A failure here is deliberately not the screen's
    // failure — the catalogue is still readable without the outlet filter, so
    // this leaves the list alone and the filter empty rather than replacing a
    // working screen with an error about a control.
    collectPages<OutletResource>((from, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  const outletItems = useMemo(
    () => [
      { label: 'All outlets', value: ANY },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  // The outlet the filter is on, or none. Looked up rather than kept in a second
  // piece of state: `outletId` is what the query is built from, and a copy of the
  // outlet beside it is a copy that can disagree with it.
  const chosenOutlet =
    outletId === ANY ? undefined : outlets.find((outlet) => outlet.id === outletId);

  // The status column earns its place only when it can discriminate. With
  // retired items hidden every row would read "On sale", which is a column of
  // one repeated word and a column staff learn to stop reading.
  const columns = includeRetired ? COLUMNS_WITHOUT_STATUS + 1 : COLUMNS_WITHOUT_STATUS;

  const filtering = kind !== ANY || outletId !== ANY;

  return (
    <Screen
      title="Catalogue"
      description="Everything the garden sells — the products on a shelf, the tickets at the gate and the activities people pay to join. What a thing costs is its price history; this is the list of things."
      action={
        mayWrite ? (
          // Two controls and **one accent**: creating an item is the screen's
          // primary action, and the file upload beside it is `outline`, per
          // `docs/ui-conventions.md` — a screen showing two green buttons has no
          // primary action, it has two equal ones.
          //
          // Both are hidden from a manager for the same reason and by the same
          // flag: every write on `CatalogueItemsController` inherits the class's
          // `@RequireRole('OWNER')`, and the upload's every request is one of
          // them. Courtesy rather than security — the route declares the role
          // again and the server refuses regardless.
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              // `nativeButton={false}` because the rendered element is an
              // anchor. Base UI otherwise warns that native button semantics
              // have been removed, and it is right to: a link claiming to be a
              // button is announced as one and then does not answer the space
              // bar.
              nativeButton={false}
              render={<Link to={paths.catalogueUpload} />}
            >
              <IconFileUpload />
              Create from a file
            </Button>

            <Button onClick={() => setCreating(true)}>
              <IconPlus />
              New item
            </Button>
          </div>
        ) : undefined
      }
      aside={
        <>
          {page !== null && state.kind !== 'failed' && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'item' : 'items'}
              {includeRetired && ', retired included'}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="kind-filter" className="text-sm text-muted-foreground">
              Kind
            </Label>
            <Select
              value={kind}
              items={[{ label: 'All kinds', value: ANY }, ...CATALOGUE_KIND_ITEMS]}
              // `null` is Base UI's "cleared", which these two pickers have no
              // affordance for — there is no clear button and no empty option.
              // Read as "not filtering" rather than cast away, so a cleared
              // select can never become a `kind=null` in the query string.
              onValueChange={(next) => {
                changeFilter(() => setKind(next ?? ANY));
              }}
            >
              <SelectTrigger id="kind-filter" aria-label="Kind" className="h-10 w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>All kinds</SelectItem>
                  {CATALOGUE_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {labelForKind(option)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="outlet-filter" className="text-sm text-muted-foreground">
              Sold at
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => setOutletId(next ?? ANY));
              }}
            >
              <SelectTrigger
                id="outlet-filter"
                aria-label="Sold at"
                // Nothing to choose between until the outlets land, and a
                // picker with one option is a control that cannot do anything.
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>All outlets</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* The way to the outlet's own catalogue (FE023), offered where
                somebody has just asked the same question of this list. The two
                answers differ and the difference is the point: this filter
                narrows *the catalogue* to what sells there, and that screen is
                what a till at that outlet holds — priced, retired items and
                withdrawn availability already gone. It appears only once an
                outlet is chosen, because there is no such thing as the catalogue
                of every outlet at once. */}
            {chosenOutlet !== undefined && (
              <Link
                to={outletCataloguePath(chosenOutlet.id)}
                className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
              >
                <IconBuildingStore className="size-4" aria-hidden />
                Everything {chosenOutlet.name} sells
              </Link>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="show-retired"
              aria-labelledby="show-retired-label"
              checked={includeRetired}
              onCheckedChange={(checked) => {
                changeFilter(() => setIncludeRetired(checked));
              }}
            />
            <Label
              id="show-retired-label"
              htmlFor="show-retired"
              className="cursor-pointer text-sm"
            >
              Show retired items
            </Label>
          </div>
        </>
      }
    >
      {notice !== null && (
        <Alert
          // `status` and not the component's default `alert`: a success is not
          // an interruption, and this is announced politely rather than cutting
          // across whatever the reader was doing.
          role="status"
        >
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{notice.code}</span> — {notice.text}
          </AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Catalogue">
        <TableHeader>
          <TableRow>
            <TableHead className="w-36">Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-36">Kind</TableHead>
            {/* Amounts right-align, per docs/ui-conventions.md — cash-up is the
                task of comparing down a column. */}
            <TableHead className="w-40 text-right">Selling price</TableHead>
            {includeRetired && <TableHead className="w-32">Status</TableHead>}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.items.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title={filtering ? 'Nothing matches these filters' : 'Nothing in the catalogue yet'}
                description={emptyDescription({ filtering, includeRetired, mayCreate: mayWrite })}
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.items.map((item) => (
                <CatalogueRow
                  key={item.id}
                  item={item}
                  showStatus={includeRetired}
                  // A manager gets no handle at all rather than a handle onto a
                  // dialog whose every control the API refuses.
                  onOpen={mayWrite ? () => setEditing(item) : undefined}
                  // And no link on the amount either, for the same reason one
                  // step further: the price history is refused outright, so the
                  // link would land on a screen that can only say no.
                  linkPrices={mayReadPrices}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {/* Not while the load failed: the notice above already says the list could
          not be read, and a pager under it offers to move through rows that are
          not there. `Pager` draws nothing when everything fits on one page. */}
      {page !== null && state.kind !== 'failed' && (
        <Pager state={page} label="catalogue items" onOffsetChange={setOffset} />
      )}

      {/* Mounted only for an owner. A manager cannot open it because there is no
          control that does, and the API would refuse the write regardless. */}
      {mayWrite && (
        <NewCatalogueItemDialog
          open={creating}
          onOpenChange={setCreating}
          outlets={outlets}
          onCreated={(item) => {
            setCreating(false);
            setNotice({ code: item.code, text: `${item.name} has been added to the catalogue.` });

            // Re-read rather than splicing the new item into the page. The API
            // decides the order, the page this row falls on, and whether
            // anything else moved while the form was open; a locally patched
            // list is a second answer to a question the server has already
            // answered.
            reload();
          }}
        />
      )}

      {/* Mounted only while a row is open, so the next item opened starts from
          its own values rather than from the last one's — and so each open gets
          its own idempotency key. `mayWrite` is checked again rather than
          relied on through `editing`: the guard belongs beside the thing it
          guards, where deleting the row control cannot silently unguard it. */}
      {mayWrite && editing !== null && (
        <EditCatalogueItemDialog
          item={editing}
          outlets={outlets}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onUpdated={(item) => {
            setEditing(null);
            setNotice({ code: item.code, text: `${item.name} has been updated.` });
            reload();
          }}
          onRetired={(item) => {
            setEditing(null);
            setNotice({
              code: item.code,
              text: `${item.name} has been retired. Its price history stays readable and its code is not released — no new item can take it. The tills that sold it stop offering it on their next check.`,
            });

            // Re-read rather than dropping the row locally. With the toggle off
            // it disappears, with it on it comes back marked — and which of
            // those is right is a question the API has just been asked.
            reload();
          }}
          onRestored={(item) => {
            setEditing(null);
            setNotice({
              code: item.code,
              text: `${item.name} is back on sale at ${formatPaise(item.sellingPaise)}, the price it was retired at. Restoring records no new price. The tills that sell it offer it again on their next check.`,
            });
            reload();
          }}
        />
      )}
    </Screen>
  );
}

/**
 * What an empty table should say, which depends on why it is empty.
 *
 * Never a bare "No results". The three cases are genuinely different — a park
 * with nothing set up, a filter that excludes everything, and a manager who
 * cannot create anything — and the sentence that helps the first is a dead end
 * for the other two.
 */
function emptyDescription({
  filtering,
  includeRetired,
  mayCreate,
}: {
  filtering: boolean;
  includeRetired: boolean;
  mayCreate: boolean;
}): string {
  if (filtering) {
    return includeRetired
      ? 'No item matches, retired ones included. Widen the kind or the outlet to see more.'
      : 'No item matches. Widen the kind or the outlet, or turn on Show retired items.';
  }

  if (!mayCreate) {
    return 'The garden has nothing on sale yet. An owner adds the first product, ticket type or activity.';
  }

  return includeRetired
    ? 'Nothing has been set up here, retired or otherwise. Products, ticket types and activities all live in this list, and New item creates the first one.'
    : 'Products, ticket types and activities all live in this list, and New item creates the first one. If some were set up and later retired, turn on Show retired items.';
}

/**
 * One row.
 *
 * **The name is the handle**, as it is on the outlets screen: a row carries no
 * buttons of its own, because the screen has one accent and it is on "New item".
 * That holds for a retired row too — `EditCatalogueItemDialog` opens it
 * read-only with the one control the API accepts against it, which is the whole
 * point of a soft delete being reachable.
 *
 * `onOpen` is optional rather than always present and sometimes inert: a manager
 * gets plain text, so there is no control to tab to and none to press.
 *
 * **The amount is the other handle**, and it opens the price history (FE022).
 * Same principle, one cell along: no button, no extra column, and the affordance
 * carried by the thing you would have clicked anyway. Two handles in one row
 * rather than one, because they answer two different questions — the name is
 * *what this item is* and the amount is *what it has cost over time* — and a
 * single control leading to a screen with tabs on it would bury the second
 * behind the first.
 *
 * `linkPrices` is separate from `onOpen` and both are decided by the caller.
 * They are not the same permission: editing is owner-only because writes are,
 * and the price history is owner-only because every row of it carries
 * `costPaise`. A manager gets neither — plain text in both cells, nothing to tab
 * to and nothing to press — rather than a control that renders and then refuses.
 */
function CatalogueRow({
  item,
  showStatus,
  onOpen,
  linkPrices,
}: {
  item: CatalogueItemResource;
  showStatus: boolean;
  onOpen?: (() => void) | undefined;

  /** Whether the amount opens this item's price history (FE022). Owner only. */
  linkPrices: boolean;
}) {
  const retired = isRetired(item);

  return (
    // A retired row is quieter, not struck through or greyed to the point of
    // being unreadable: it is history that must stay legible, because the whole
    // reason for keeping it reachable is that somebody is looking something up.
    // `text-muted-foreground` is safe here — the row sits on `bg-card`, and it
    // is only the muted-on-muted pairing that misses AA.
    <TableRow className={retired ? 'text-muted-foreground' : undefined}>
      {/* A code is read aloud and typed back in, so it gets the mono face and
          tabular figures — and this one is on receipts and tickets that outlive
          the record, which is exactly the column somebody reads down. */}
      <TableCell className={`font-mono numeric ${retired ? '' : 'text-foreground'}`}>
        {item.code}
      </TableCell>
      <TableCell className={`whitespace-normal ${retired ? '' : 'text-foreground'}`}>
        {onOpen === undefined ? (
          item.name
        ) : (
          // A control with no chrome: the accent on this screen belongs to "New
          // item". Underlined on hover and focus so it is discoverable as a
          // control without a page of them competing with the one primary
          // action. The amount below is the row's other handle and follows the
          // same rule — no button, no column of its own.
          <button
            type="button"
            className="rounded-sm text-left underline-offset-4 outline-none hover:underline focus-visible:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            onClick={onOpen}
          >
            {item.name}
          </button>
        )}
      </TableCell>
      <TableCell>{labelForKind(item.kind)}</TableCell>
      {/* The amount *is* the way into the price history, rather than a control
          in a column of its own. Two reasons. The screen's description already
          says "what a thing costs is its price history; this is the list of
          things" — this makes that sentence true by making the amount the thing
          you follow. And a row's affordances live on its own cells here, as the
          name above does: an actions column would be fifty more targets
          competing with the screen's one accent, on a table whose density
          `docs/design-language.md` has not settled yet.

          A dotted underline in the hairline colour rather than the name's solid
          one on hover, which is the two handles telling themselves apart:
          following the amount navigates away, and pressing the name opens a
          dialog over the list. Enough to read as followable without turning a
          column of amounts into a column of links. */}
      <TableCell className={`text-right font-mono numeric ${retired ? '' : 'text-foreground'}`}>
        {linkPrices ? (
          <Link
            to={cataloguePricesPath(item.id)}
            // Named per row, because "₹25.00" repeated down a list of links is
            // no help to somebody navigating by them.
            aria-label={`Price history for ${item.name}`}
            className="rounded-sm underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            {formatPaise(item.sellingPaise)}
          </Link>
        ) : (
          formatPaise(item.sellingPaise)
        )}
      </TableCell>

      {showStatus && (
        <TableCell>
          {retired ? (
            // `outline` and not `destructive`. Retiring an item is an ordinary
            // end to an ordinary life, and red is reserved for a system that
            // refused something or is about to.
            <Badge variant="outline">Retired</Badge>
          ) : (
            <span className="text-muted-foreground">On sale</span>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
